import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import moment from "moment";
import useLocationQueryParams from "../../../v2/hooks/useLocationQueryParams";
import { useDispatch, useSelector } from "react-redux";
import { getChannels } from "./Utilities/analyzeUtils";
import FilterForm from "./Utilities/FilterForm";
import { dataSourceHandleActions, messageActions } from "../../../../_actions";
import BasicTextField from "../../../v2/common/BasicTextField";
import SearchIcon from "@mui/icons-material/Search";
import ActionButton from "../../../v2/common/ActionButton";
import CircularProgress from "@mui/material/CircularProgress";
import useGetSearchResult from "./Utilities/hooks/useGetSearchResult";
import importedStyles from "../../../v2/views/Proactive/Utilities/templateStyles";
import { pluralize } from "../../../v2/views/ProactiveMetricsDetail/ProactiveMetricsDetailUtil";
import BasicTable from "components/v2/common/BasicTable";
import useGetSearchSummary from "./Utilities/hooks/useGetSearchSummary";
import HighlightCard from "./Utilities/HighlightCard";
import HighlightLoader from "./Utilities/HighlightLoader";

export default function Analyze() {
  const ROWS_PER_PAGE = 10;
  const currentUser = JSON.parse(localStorage.user);
  const { params, setParams } = useLocationQueryParams();
  const dispatch = useDispatch();
  const dataSourceHandles = useSelector(
    (state) => state.dataSourceHandles.dataSourceHandles,
  );
  const exporting = useSelector((state) => state.search?.exporting);

  const derivedParams = useMemo(() => {
    const start = params.startDate
      ? moment(params.startDate).startOf("day")
      : moment().startOf("year");
    const end = params.endDate ? moment(params.endDate).endOf("day") : moment();
    const _channels = params.channels
      ? params.channels.split(",").map(Number)
      : [];

    return {
      startDate: start.format("YYYY-MM-DD HH:mm:ss"),
      endDate: end.format("YYYY-MM-DD HH:mm:ss"),
      channels: _channels,
      searchString: params.searchString || "",
      offset: Number(params.offset || 0),
      page: Number(params.page || 0),
      direction: params.direction || "INCOMING",
    };
  }, [params]);

  const { channelsList, channelIds } = useMemo(() => {
    if (!dataSourceHandles) return { channelsList: [], channelIds: [] };

    return {
      channelsList: getChannels(dataSourceHandles),
      channelIds: getChannels(dataSourceHandles, "ids"),
    };
  }, [dataSourceHandles]);

  const [searchInput, setSearchInput] = useState(derivedParams.searchString);
  const [filterParams, setFilterParams] = useState({
    startDate: derivedParams.startDate,
    endDate: derivedParams.endDate,
    organization: [currentUser._embedded.organization.trendsId],
    channels: derivedParams.channels,
    offset: derivedParams.offset,
    direction: derivedParams.direction,
    page: derivedParams.page,
  });

  const buildSearchParams = useCallback(
    ({ filters, searchString, page = 0, offset = 0 }) => {
      const _channels =
        filters.channels?.length > 0
          ? filters.channels
          : getChannels(dataSourceHandles, "ids");

      return {
        organizations: filters.organization,
        channels: _channels,
        direction: filters.direction,
        searchString,
        startDate: moment(filters.startDate).startOf("day").toISOString(),
        endDate: moment(filters.endDate).endOf("day").toISOString(),
        offset,
        page,
        show: true,
      };
    },
    [dataSourceHandles],
  );

  const handlePageChange = useCallback(
    (e, nextPage) => {
      const nextOffset = (nextPage || 0) * ROWS_PER_PAGE;

      const updatedFilters = {
        ...filterParams,
        page: nextPage,
        offset: nextOffset,
      };
      setFilterParams(updatedFilters);

      const params = buildSearchParams({
        filters: filterParams,
        searchString: searchInput,
        page: nextPage,
        offset: nextOffset,
      });

      dispatch(messageActions.searchMessages(params));
      setParams(params);
    },
    [filterParams, searchInput, buildSearchParams],
  );

  const handleSubmit = useCallback(() => {
    const nextFilters = {
      ...filterParams,
      page: 0,
      offset: 0,
    };

    setFilterParams(nextFilters);

    const params = buildSearchParams({
      filters: nextFilters,
      searchString: searchInput,
    });

    dispatch(messageActions.clearVisitedPages());
    dispatch(messageActions.searchMessages(params));
    dispatch(messageActions.getSearchSummary(params));
    setParams(params);
  }, [buildSearchParams, filterParams, searchInput, setParams, dispatch]);

  const handleSearchOnKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleSearchField = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const handleTableCellClick = useCallback((params) => {
    if (params) {
      window.location.replace(params);
    }
  }, []);

  const handleExport = useCallback(() => {
    const exportParams = buildSearchParams({
      filters: filterParams,
      searchString: searchInput,
      page: 0,
      offset: 0,
    });

    dispatch(messageActions.exportAdminSearch(exportParams));
  }, [buildSearchParams, filterParams, searchInput, dispatch]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const id = user?._embedded?.organization?.id;

    if (id) {
      dispatch(
        dataSourceHandleActions.getAll({
          organization: id,
        }),
      );
    }
  }, [dispatch]);

  const { rows, columns, totalCount, loading } = useGetSearchResult();
  const { searchSummary, loadingSummary } = useGetSearchSummary();

  return (
    <Box sx={styles.rootWrapper}>
      <Box sx={styles.container}>
        <Box sx={styles.containerLeft}>
          <FilterForm
            startDate={filterParams.startDate}
            endDate={filterParams.endDate}
            channelsFilter={channelsList}
            channelsAction={filterParams.channels}
            channelsIds={channelIds}
            direction={filterParams.direction}
            onChange={(partial) => {
              setFilterParams((prev) => ({ ...prev, ...partial }));
            }}
            handleSubmit={handleSubmit}
          />
        </Box>

        <Box sx={styles.containerRight}>
          <Box
            sx={{
              width: "100%",
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
            }}
          >
            <BasicTextField
              name={"searchMessage"}
              startIcon={<SearchIcon sx={styles.icon} />}
              placeholder={"Search"}
              value={searchInput}
              handleChange={handleSearchField}
              onKeyDown={handleSearchOnKeyDown}
              sx={styles.textField}
            />
          </Box>

          {loadingSummary && (
            <Box mt={{ xs: "14px", sm: "20px" }}>
              <HighlightLoader />
            </Box>
          )}

          {!loadingSummary && searchSummary?.data?.response && (
            <Box mt="14px">
              <HighlightCard
                params={filterParams}
                summary={searchSummary.data.response}
              />
            </Box>
          )}

          {rows.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "24px 0 14px 0",
                gap: "16px",
              }}
            >
              <Box component="p" sx={styles.resultText}>
                {totalCount} {pluralize("result", totalCount)}
              </Box>
              <Box sx={{ width: "max-content" }}>
                <ActionButton
                  label={exporting ? "Exporting..." : "Export"}
                  action={handleExport}
                  disabled={exporting}
                  withIcon={false}
                />
              </Box>
            </Box>
          )}

          {loading && (
            <Box
              sx={importedStyles.emptyContainer}
              mt={{ xs: "14px", sm: "20px" }}
            >
              <CircularProgress size={25} sx={{ color: "#003300" }} />
            </Box>
          )}

          {rows.length > 0 && (
            <Box mt="14px">
              <BasicTable
                rows={rows}
                columns={columns}
                totalCount={totalCount}
                isPaginationDisabled={loading}
                page={filterParams?.page}
                handlePageChange={handlePageChange}
                handleTableCellClick={handleTableCellClick}
                tableHeadStyle={styles.ellipsis}
                hideHeader={true}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const styles = {
  rootWrapper: {
    backgroundColor: "#EDEDE4",
    pt: 16,
    pb: 10,
    minHeight: "100vh",
  },
  container: {
    px: { xs: 2, sm: 6 },
    maxWidth: "1440px",
    display: "flex",
    gap: "40px",
    alignItems: "flex-start",
    flexDirection: { xs: "column", md: "row" },
  },
  containerLeft: {
    minWidth: "290px",
    maxWidth: "290px",
    padding: "33px",
    background: "#FFFFFF",
    borderRadius: "8px",
    border: "1px solid #dee2e0",
    boxShadow: "0 2px 4px 0 rgba(196, 201, 199, 0.5)",
  },
  containerRight: {
    width: "100%",
  },
  resultText: {
    fontFamily: "FustatMedium",
    fontSize: "16px",
    color: "#302E2A",
  },
  searchButton: {
    width: "100px",
    height: "38px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  textField: {
    height: "50px",
    [`& fieldset`]: {
      borderRadius: "8px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderWidth: "1.5px",
        borderColor: "#9EA9A9",
      },
      "&:hover fieldset": {
        borderColor: "#687979",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#003300",
      },
    },
  },
  icon: {
    color: "#495D5E",
  },
  ellipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};
